import { motion } from "framer-motion";
import { CheckCircle2, Heart } from "lucide-react";
import AnimatedCounter from "../components/AnimatedCounter";
import TrustBadge from "../components/TrustBadge";
import { staggerContainer, fadeUp, fadeLeft } from "../animations/variants";

const stats = [
  { value: 300000, suffix: "+", label: "Scans Completed", icon: "🔬" },
  { value: 30, suffix: "+", label: "Years of Trust", icon: "🏆" },
  { value: 99, suffix: ".8%", label: "Accuracy Rate", icon: "✅" },
  { value: 24, suffix: "/7", label: "Emergency Service", icon: "🚑" },
];

const trustPoints = [
  "30+ years of diagnostic excellence in North Telangana",
  "Experienced radiologist and qualified technologist",
  "Same-day report delivery for most scans",
  "Affordable pricing with no compromise on quality",
  "State-of-the-art 1.5 Tesla Hdxt MRI & 32-Slice CT",
];

export default function AboutSection() {
  return (
    <section id="about" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left visual */}
          <motion.div
            variants={fadeLeft}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            className="relative"
          >
            {/* Main card */}
            <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-primary-700 to-primary-900 p-10 text-white shadow-2xl">
              {/* Decorative circle */}
              <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full bg-white/5" />
              <div className="absolute -bottom-8 -left-8 w-40 h-40 rounded-full bg-sky-400/10" />

              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 text-sm text-sky-200 mb-8">
                  <Heart className="w-3.5 h-3.5" /> Trusted by Thousands
                </div>
                <h3 className="font-poppins font-bold text-3xl mb-4">
                  Serving North Telangana Since 1996
                </h3>
                <p className="text-white/70 text-base leading-relaxed mb-8">
                  Indur CT & MRI Centre has been the region's most trusted
                  diagnostic imaging facility for over 30 years. Our commitment
                  to accuracy, compassion, and cutting-edge technology makes us
                  the first choice of physicians and patients alike.
                </p>
                {/* Inline stats */}
                <div className="grid grid-cols-2 gap-4">
                  {stats.slice(0, 2).map((s) => (
                    <div
                      key={s.label}
                      className="bg-white/10 rounded-2xl p-4 border border-white/10"
                    >
                      <div className="text-3xl font-poppins font-bold text-sky-300 mb-1">
                        <AnimatedCounter target={s.value} suffix={s.suffix} />
                      </div>
                      <div className="text-white/60 text-sm">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Floating Trust Badge — exact brand asset */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -bottom-10 -right-6 lg:-right-14 drop-shadow-2xl"
            >
              <TrustBadge size={168} animate={false} />
            </motion.div>
          </motion.div>

          {/* Right content */}
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
          >
            <motion.p variants={fadeUp} className="section-subtitle">
              Who We Are
            </motion.p>
            <motion.h2
              variants={fadeUp}
              className="section-title text-gray-900 mb-5"
            >
              Diagnostic Excellence{" "}
              <span className="gradient-text">You Can Trust</span>
            </motion.h2>
            <motion.p
              variants={fadeUp}
              className="text-gray-500 text-lg leading-relaxed mb-8"
            >
              At Indur CT & MRI Centre, Doctor Line, Nizamabad, we combine
              advanced technology with compassionate care. Our team of expert
              radiologists ensures every report is precise, timely, and
              actionable.
            </motion.p>

            {/* Trust points */}
            <motion.ul variants={staggerContainer} className="space-y-3 mb-10">
              {trustPoints.map((point) => (
                <motion.li
                  key={point}
                  variants={fadeUp}
                  className="flex items-start gap-3"
                >
                  <CheckCircle2 className="w-5 h-5 text-primary-500 shrink-0 mt-0.5" />
                  <span className="text-gray-600">{point}</span>
                </motion.li>
              ))}
            </motion.ul>

            {/* Stats row */}
            <motion.div
              variants={fadeUp}
              className="grid grid-cols-2 sm:grid-cols-4 gap-4"
            >
              {stats.map((s) => (
                <div
                  key={s.label}
                  className="text-center p-4 bg-gray-50 rounded-2xl border border-gray-100"
                >
                  <div className="text-2xl mb-1">{s.icon}</div>
                  <div className="font-poppins font-bold text-2xl text-primary-600">
                    <AnimatedCounter target={s.value} suffix={s.suffix} />
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{s.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
